'use client';

import { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { WordReveal } from '@/components/motion/WordReveal';
import styles from './LegalIntro.module.css';

const EASE = [0.22, 1, 0.36, 1] as const;

interface LegalIntroProps {
  eyebrow: string;
  title: string;
  description: string;
  lastUpdatedLabel?: string;
  lastUpdated?: string;
  centered?: boolean;
}

export function LegalIntro({
  eyebrow,
  title,
  description,
  lastUpdatedLabel,
  lastUpdated,
  centered = false,
}: LegalIntroProps) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const cls = `${styles.intro} ${centered ? styles.centered : ''}`;

  /* Server + first hydration: render static to avoid flash */
  if (!mounted || reduced) {
    return (
      <header className={cls}>
        <span className={styles.eyebrow}>{eyebrow}</span>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.ornament} aria-hidden="true">
          <span className={styles.ornamentLine} />
          <span className={styles.ornamentDiamond} />
          <span className={styles.ornamentLine} />
        </div>
        <p className={styles.description}>{description}</p>
        {lastUpdated && lastUpdatedLabel ? (
          <p className={styles.date}>{lastUpdatedLabel} : {lastUpdated}</p>
        ) : null}
      </header>
    );
  }

  /* Client after mount: full entrance sequence */
  return (
    <header className={cls}>
      <motion.span
        className={styles.eyebrow}
        initial={{ opacity: 0, x: -14 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
      >
        {eyebrow}
      </motion.span>

      <h1 className={styles.title}>
        <WordReveal text={title} delay={0.22} stagger={0.09} />
      </h1>

      <motion.div
        className={styles.ornament}
        aria-hidden="true"
        initial={{ opacity: 0, scaleX: 0.3 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.75, delay: 0.58, ease: EASE }}
        style={{ transformOrigin: centered ? 'center' : 'left center' }}
      >
        <span className={styles.ornamentLine} />
        <span className={styles.ornamentDiamond} />
        <span className={styles.ornamentLine} />
      </motion.div>

      <motion.p
        className={styles.description}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
      >
        {description}
      </motion.p>

      {lastUpdated && lastUpdatedLabel ? (
        <motion.p
          className={styles.date}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.9, ease: EASE }}
        >
          {lastUpdatedLabel} : {lastUpdated}
        </motion.p>
      ) : null}
    </header>
  );
}
